import { apiUrl, apiUrlV2 } from "../constants/apiUrl"
import sortUnitsByNapIssues from "../utilities/sortUnitsByNapIssues"
import { handleCsvDownload } from "../utilities/handleCsvDownload"

const initialState = {
  units: [],
  unit: undefined,
  comingDueUnits: [],
  soldUnits: [],
  amountDueOn: {},
  isFetchingAmountDue: false,
  unitsFetching: false,
  unitFetching: false,
  dashboardUnitId: null,
  unitTitle: null,
  downloadingPreviousUnits: false,
  previousUnits: [],
  markingUnitSold: false,
  downloadingUnits: false,
  markingUnitUnSold: false,
  markingUnitArbitrated: false,
  markingUnitUnarbitrated: false
}

const createUnitsSlice = (set, get) => ({
  ...initialState,
  fetchUnits: async () => {
    set((state) => ({ unitsFetching: true }))

    try {
      const response = await fetch(
        `${apiUrlV2}/units?filter[scope]=current&sort[for_dealers]=true&per_page=1000`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()

      set((state) => ({ units: data.data }))
    } catch (err) {
      get().logError('Unit data was not able to be fetched.', err)
    } finally {
      set(() => ({ unitsFetching: false }))
    }
  },
  fetchPreviousUnits: async (startOn, endOn, reportType) => {
    set((state) => ({ downloadingPreviousUnits: true }))

    try {
      const response = await fetch(
        `${apiUrl}/units/previous-inventory-report?report_type=${reportType}&start_on=${startOn}&end_on=${endOn}`,
        {
          headers: get().requestHeaders,
          method: "POST"
        }
      )

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      if (reportType === 'PDF') {
        link.download = `PreviousInventoryReport${startOn}-${endOn}.pdf`
      } else {
        link.download = `PreviousInventoryReport${startOn}-${endOn}.xls`
      }
      link.click()
    } catch (err) {
      get().logError('Error downloading Previous Units PDF', err)
    } finally {
      set(() => ({ downloadingPreviousUnits: false }))
    }
  },
  fetchUnit: async (unitId) => {
    set(() => ({ unitFetching:true }))

    try {
      const response = await fetch(
        `${apiUrlV2}/units/${unitId}`,
        { headers: get().requestHeaders }
      )
      const data = await response.json()

      if (data.errors) {
        set((state) => ({ unit: data }))
      } else {
        set((state) => ({ unit: data.data }))
      }
    } catch (err) {
      get().logError('Error fetching unit.', err)
    } finally {
      set(() => ({ unitFetching: false }))
    }
  },
  markUnitSold: async (unit, soldOn, soldTo, saleType, fundingSource = '') => {
    const isEmployee = get().user.type === 'AccountManager' || get().user.type === 'VCI'
    const dealerId = unit?.attributes['dealer-id']

    set(() => ({ markingUnitSold: true }))
    try {
      const response = await fetch(
        (isEmployee ? `${apiUrlV2}/dealers/${dealerId}/units/${unit.id}/sold` : `${apiUrl}/units/${unit.id}/sold`),
        {
          headers: get().requestHeaders,
          method: "PUT",
          body: JSON.stringify({ unit: {sold_on: soldOn, sold_to: soldTo, sale_type: saleType, funding_source: fundingSource} })
        }
      )
      const data = await response.json()
      set((state) => ({ unit: data.data }))

      if (isEmployee) {
        get().fetchUnitsAsAm(dealerId)
      } else {
        get().fetchUnits()
      }
    } catch (err) {
      get().logError('There was an error marking unit sold.', err)
    } finally {
      set(() => ({ markingUnitSold: false }))
    }
  },
  markUnitUnsold: async (unit) => {
    const dealerId = unit?.attributes['dealer-id']

    set(() => ({ markingUnitUnSold: true }))
    try {
      const response = await fetch(
        (`${apiUrlV2}/dealers/${dealerId}/units/${unit.id}/mark_unsold`),
        {
          headers: get().requestHeaders,
          method: "PUT"
        }
      )
      const data = await response.json()
      set((state) => ({ unit: data.data }))
      get().fetchUnitsAsAm(dealerId)

    } catch (err) {
      get().logError('There was an error marking unit unsold.', err)
    } finally {
      set(() => ({ markingUnitUnSold: false }))
    }
  },
  markUnitArbitrated: async (unit, soldOn, note) => {
    const dealerId = unit?.attributes['dealer-id']

    set(() => ({ markingUnitArbitrated: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/dealers/${dealerId}/units/${unit.id}/arbitrated`,
        {
          headers: get().requestHeaders,
          method: "PUT",
          body: JSON.stringify({ unit: { sold_on: soldOn, note: note } })
        }
      )
      const data = await response.json()
      set((state) => ({ unit: data.data }))
      get().setSuccessMessage('Unit successfully marked arbitrated')
      get().fetchUnitsAsAm(dealerId)

      return true
    } catch (err) {
      get().logError('There was an error marking unit arbitrated.', err)
      get().setErrMessage('There was an error marking unit arbitrated.')

      return false
    } finally {
      set(() => ({ markingUnitArbitrated: false }))
    }
  },
  markUnitUnarbitrated: async (unit) => {
    const dealerId = unit?.attributes['dealer-id']

    set(() => ({ markingUnitUnarbitrated: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/dealers/${dealerId}/units/${unit.id}/unarbitrated`,
        {
          headers: get().requestHeaders,
          method: "PUT"
        }
      )
      const data = await response.json()
      set((state) => ({ unit: data.data }))
      get().setSuccessMessage('Unit successfully marked unarbitrated')
      get().fetchUnitsAsAm(dealerId)

      return true
    } catch (err) {
      get().logError('There was an error marking unit unarbitrated.', err)
      get().setErrMessage('There was an error marking unit unarbitrated.')

      return false
    } finally {
      set(() => ({ markingUnitUnarbitrated: false }))
    }
  },
  fetchAmountDueOn: (unitId, date, couponRedemptionId) => {
    set((state) => ({ isFetchingAmountDue: true }))
    fetch(
      `${apiUrl}/units/${unitId}/amounts_due_on?amounts_due_on_date=${date}&coupon_redemption_id=${couponRedemptionId}`,
      { headers: get().requestHeaders }
    )
      .then(response => response.json())
      .then(data => {
        if (String(get().unit?.id) === String(data.id)) {
          set((state) => ({ amountDueOn: data, isFetchingAmountDue: false  }))
        }
      })
  },
  cancelNap: (unitId) => fetch(
    `${apiUrlV2}/units/${unitId}/cancel_nap`, {
      method: 'PUT',
      headers: get().requestHeaders
    }
  ),
  updateUnit: (unitId, body) => fetch(
    `${apiUrlV2}/units/${unitId}`, {
      method: 'PUT',
      headers: { 'Authorization': get().requestHeaders['Authorization']},
      body: body
    }
  ),
  pendingAndHistoricNaps: () => {
    return(
      get().units?.filter((unit) => unit.attributes['created-by-type'] === 'DealerUser')?.reverse()
    )
  },
  setUnit: (unit) => set((state) => ({ unit: unit })),
  setDashboardUnitId: (id) => set(() => ({ dashboardUnitId: id })),
  fetchUnitTitle: (unitId) => fetch(
    `${apiUrl}/units/${unitId}/title`, {
      method: 'POST',
      headers: get().requestHeaders
    }
  ),
  downloadCurrentUnits: async(user, today) => {
    set(() => ({ downloadingUnits: true }))

    const url = `${apiUrlV2}/units.csv?filter[scope]=current&sort[for_dealers]=true`
    const fileName = `${user.lotname}-current-units-${today}.csv`

    const responseSuccess = await handleCsvDownload(url, fileName, get().requestHeaders)

    if (responseSuccess) {
      get().setSuccessMessage('Successfully downloaded current units')
    } else {
      get().logError('There was an error downloading current units.', err)
    }

    set(() => ({ downloadingUnits: false }))
  },
  createTitleRequestTicket: async(unitId, body) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/units/${unitId}/create_title_request_ticket`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify(body)
        }
      )
      const data = await response.json()
      if (response.status === 200) {
        get().setSuccessMessage('Title request ticket successfully created')
        get().fetchUnit(unitId)
      } else {
        get().setErrMessage(data.errors[0])
      }
    } catch (err) {
      get().logError('There was an error creating title request ticket.', err)
      get().setErrMessage('There was an error creating title request ticket.')
    }
  },
  resetUnitsSlice: () => set(initialState)
})

export default createUnitsSlice